import React, { useMemo, useRef, useState } from "react";
import classNames from "classnames";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import {
  ShipmentItem,
  StageKey,
  LOGI_STYLE,
  PAY_STYLE,
  money,
  kg,
  parseFrDateTime,
  formatFrDateTime,
} from "./envois-shared";

/* -------------------------------------------------------------------------- */
/*                                   Stages                                   */
/* -------------------------------------------------------------------------- */

export const STAGES: { key: StageKey; title: string; dot: string }[] = [
  { key: "prospect", title: "Prospect / non payé", dot: "#ef4444" },
  { key: "paid", title: "Payé — à confirmer", dot: "#f59e0b" },
  { key: "confirmed", title: "Confirmé — réceptionné", dot: "#7c3aed" },
  { key: "shipped", title: "Expédié", dot: "#6366f1" },
  { key: "transit", title: "En transit / douane", dot: "#0ea5e9" },
  { key: "correspondent", title: "Chez le correspondant", dot: "#14b8a6" },
  { key: "delivered", title: "Livré", dot: "#10b981" },
  { key: "cancelled", title: "Annulé / remboursé", dot: "#94a3b8" },
];

const stageTitle = (key: StageKey) => STAGES.find((s) => s.key === key)?.title ?? key;

// Column of a shipment that has never been moved on the board, inferred from its statuses.
function deriveStage(r: ShipmentItem): StageKey {
  if (r.logi === "Annulé" || r.pay === "Remboursé") return "cancelled";
  if (r.logi === "Livré") return "delivered";
  if (r.logi === "Chez le correspondant") return "correspondent";
  if (r.logi === "En transit" || r.logi === "Dédouanement") return "transit";
  if (r.logi === "Expédié") return "shipped";
  if (r.pay === "Non payé") return "prospect";
  if (!r.confirmed) return "paid";
  return "confirmed";
}

export const stageOf = (r: ShipmentItem): StageKey => r.stage ?? deriveStage(r);

/* -------------------------------------------------------------------------- */
/*                        Transition forms (per target)                       */
/* -------------------------------------------------------------------------- */

type FieldDef = {
  name: string;
  label: string;
  type: "text" | "number" | "date" | "select" | "textarea";
  required?: boolean;
  options?: string[];
  placeholder?: string;
  initial?: (r: ShipmentItem) => string;
};

type TransitionDef = {
  intro: string;
  fields: FieldDef[];
  warning?: (r: ShipmentItem) => string | null;
  validate?: (v: Record<string, string>, r: ShipmentItem) => string | null;
  apply: (v: Record<string, string>, r: ShipmentItem) => Partial<ShipmentItem>;
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const isoToFr = (iso: string) => (iso ? iso.split("-").reverse().join("/") : "");
const isoToDayMonth = (iso: string) => isoToFr(iso).slice(0, 5);

const PAYMENT_CHANNELS = ["Orange Money", "MTN MoMo", "Touch / ITX", "Espèces", "Virement bancaire"];

const TRANSITIONS: Record<StageKey, TransitionDef> = {
  prospect: {
    intro: "Le dossier repasse en prospect. Indique pourquoi.",
    fields: [{ name: "motif", label: "Motif du retour en prospect", type: "textarea", required: true }],
    apply: (v) => ({ confirmed: false, logi: "Attente dépôt", logiInfo: `Retour prospect — ${v.motif}` }),
  },
  paid: {
    intro: "Enregistre le paiement reçu du client.",
    fields: [
      { name: "montant", label: "Montant encaissé (FCFA)", type: "number", required: true, initial: (r) => String(r.paid > 0 ? r.paid : r.montant) },
      { name: "canal", label: "Canal de paiement", type: "select", required: true, options: PAYMENT_CHANNELS, initial: (r) => (PAYMENT_CHANNELS.includes(r.canal) ? r.canal : "") },
      { name: "reference", label: "Référence de transaction", type: "text", required: true, placeholder: "ex. MP260928.1432.A1234", initial: (r) => (r.refOM !== "—" ? r.refOM : r.refTouch !== "—" ? r.refTouch : "") },
    ],
    validate: (v) => (Number(v.montant) > 0 ? null : "Le montant encaissé doit être supérieur à 0."),
    apply: (v, r) => {
      const paid = Math.min(Number(v.montant), r.montant);
      const isTouch = v.canal === "Touch / ITX";
      return {
        paid,
        pay: paid >= r.montant ? "Payé" : "Partiel",
        canal: v.canal,
        refOM: isTouch ? r.refOM : v.reference,
        refTouch: isTouch ? v.reference : r.refTouch,
        confirmed: false,
      };
    },
  },
  confirmed: {
    intro: "Confirme la réception physique du colis en agence.",
    fields: [
      { name: "date", label: "Date de réception", type: "date", required: true, initial: todayIso },
      { name: "agence", label: "Agence / entrepôt de réception", type: "text", required: true, placeholder: "ex. Entrepôt Douala Bonabéri" },
      { name: "poids", label: "Poids vérifié (kg)", type: "number", required: true, initial: (r) => String(r.poids) },
      { name: "colis", label: "Nombre de colis", type: "number", required: true, initial: (r) => String(r.colis) },
    ],
    warning: (r) => (r.pay === "Non payé" ? "Aucun paiement n'est enregistré pour cette expédition." : null),
    validate: (v) => (Number(v.poids) > 0 && Number(v.colis) > 0 ? null : "Le poids et le nombre de colis doivent être supérieurs à 0."),
    apply: (v) => ({
      confirmed: true,
      logi: "Réceptionné",
      poids: Number(v.poids),
      colis: Number(v.colis),
      logiInfo: `Réceptionné le ${isoToFr(v.date)} · ${v.agence}`,
    }),
  },
  shipped: {
    intro: "Renseigne le document de transport et le départ.",
    fields: [
      { name: "awb", label: "N° AWB / BL", type: "text", required: true, placeholder: "ex. AWB 057-4821 3390", initial: (r) => r.awb },
      { name: "transporteur", label: "Transporteur · vol / navire / camion", type: "text", required: true, placeholder: "ex. Vol AF 947", initial: (r) => r.departInfo.split("·")[0].trim() },
      { name: "date", label: "Date de départ", type: "date", required: true, initial: (r) => (/^\d{4}-\d{2}-\d{2}$/.test(r.depart) ? r.depart : todayIso()) },
    ],
    warning: (r) => (!r.confirmed ? "Le colis n'a pas encore été confirmé / réceptionné en agence." : null),
    apply: (v) => ({
      awb: v.awb,
      awbBy: "Vous",
      awbAt: formatFrDateTime(new Date()),
      depart: v.date,
      departInfo: v.transporteur,
      logi: "Expédié",
      logiInfo: `Parti le ${isoToFr(v.date)} · ${v.transporteur}`,
    }),
  },
  transit: {
    intro: "Mets à jour la position de l'expédition.",
    fields: [
      { name: "statut", label: "Statut", type: "select", required: true, options: ["En transit", "Dédouanement"], initial: (r) => (r.logi === "Dédouanement" ? "Dédouanement" : "En transit") },
      { name: "localisation", label: "Localisation actuelle", type: "text", required: true, placeholder: "ex. Hub CDG Paris" },
      { name: "eta", label: "Arrivée estimée (ETA)", type: "date", required: true },
    ],
    warning: (r) => (!r.awb ? "Aucun AWB / BL n'est enregistré pour cette expédition." : null),
    apply: (v) => ({ logi: v.statut as ShipmentItem["logi"], logiInfo: v.localisation, eta: isoToDayMonth(v.eta) }),
  },
  correspondent: {
    intro: "L'expédition est arrivée chez le correspondant à destination.",
    fields: [
      { name: "correspondant", label: "Correspondant / agence de destination", type: "text", required: true, placeholder: "ex. TCCE Paris — Aubervilliers" },
      { name: "date", label: "Date d'arrivée", type: "date", required: true, initial: todayIso },
      { name: "contact", label: "Contact du correspondant", type: "text", placeholder: "ex. +33 6 …" },
    ],
    apply: (v) => ({
      logi: "Chez le correspondant",
      logiInfo: `Arrivé le ${isoToFr(v.date)} · ${v.correspondant}${v.contact ? ` (${v.contact})` : ""}`,
    }),
  },
  delivered: {
    intro: "Confirme la remise au destinataire.",
    fields: [
      { name: "date", label: "Date de livraison", type: "date", required: true, initial: todayIso },
      { name: "receptionnaire", label: "Remis à (nom du réceptionnaire)", type: "text", required: true, initial: (r) => r.receiver },
      { name: "piece", label: "Pièce d'identité / n° de preuve", type: "text" },
    ],
    warning: (r) => (r.montant - r.paid > 0 && r.pay !== "Remboursé" ? `Il reste ${money(r.montant - r.paid)} à encaisser.` : null),
    apply: (v) => ({
      logi: "Livré",
      logiInfo: `Livré le ${isoToFr(v.date)} à ${v.receptionnaire}${v.piece ? ` · ${v.piece}` : ""}`,
    }),
  },
  cancelled: {
    intro: "Annule l'expédition et précise le remboursement éventuel.",
    fields: [
      { name: "motif", label: "Motif d'annulation", type: "textarea", required: true },
      { name: "remboursement", label: "Remboursement", type: "select", required: true, options: ["Aucun remboursement", "Remboursement total", "Remboursement partiel"], initial: () => "Aucun remboursement" },
      { name: "montantRembourse", label: "Montant remboursé (FCFA)", type: "number", placeholder: "Obligatoire si remboursement partiel" },
    ],
    validate: (v) =>
      v.remboursement === "Remboursement partiel" && !(Number(v.montantRembourse) > 0)
        ? "Indique le montant remboursé pour un remboursement partiel."
        : null,
    apply: (v, r) => ({
      logi: "Annulé",
      logiInfo: v.motif,
      pay: v.remboursement === "Aucun remboursement" ? r.pay : "Remboursé",
    }),
  },
};

function TransitionModal({
  item,
  from,
  to,
  onCancel,
  onConfirm,
}: {
  item: ShipmentItem;
  from: StageKey;
  to: StageKey;
  onCancel: () => void;
  onConfirm: (patch: Partial<ShipmentItem>, note: string) => void;
}) {
  const def = TRANSITIONS[to];
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = { commentaire: "" };
    def.fields.forEach((f) => (init[f.name] = f.initial ? f.initial(item) : ""));
    return init;
  });
  const [error, setError] = useState<string | null>(null);
  const warning = def.warning?.(item) ?? null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const missing = def.fields.filter((f) => f.required && !values[f.name]?.trim());
    if (missing.length) {
      setError(`Champs obligatoires : ${missing.map((f) => f.label).join(", ")}.`);
      return;
    }
    const invalid = def.validate?.(values, item);
    if (invalid) {
      setError(invalid);
      return;
    }
    const comment = values.commentaire.trim();
    onConfirm(def.apply(values, item), `${stageTitle(from)} → ${stageTitle(to)}${comment ? ` (${comment})` : ""}`);
  };

  const inputCls =
    "w-full h-9 px-2.5 bg-white border border-gray-200 rounded-lg text-xs text-[#1f1b2e] focus:outline-none focus:ring-2 focus:ring-purple-300";

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onMouseDown={onCancel}>
      <form
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden text-xs"
      >
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold text-gray-500 flex items-center gap-1.5 flex-wrap">
              <span>{stageTitle(from)}</span>
              <ChevronRightIcon className="w-3 h-3" />
              <span className="text-purple-700">{stageTitle(to)}</span>
            </div>
            <h3 className="text-base font-bold text-[#1f1b2e] mt-1">
              Changement de bloc · <span className="font-mono text-emerald-800">{item.code}</span>
            </h3>
            <p className="text-gray-500 mt-0.5">
              {item.sender} · {item.from} → {item.to}
            </p>
          </div>
          <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-gray-100 text-gray-500" title="Annuler">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
          <p className="text-gray-600">{def.intro}</p>
          {warning && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
              <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-px" />
              <span>{warning}</span>
            </div>
          )}
          {def.fields.map((f) => (
            <label key={f.name} className="block">
              <span className="block font-semibold text-gray-700 mb-1">
                {f.label} {f.required && <span className="text-rose-600">*</span>}
              </span>
              {f.type === "select" ? (
                <select
                  value={values[f.name]}
                  onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                  className={inputCls}
                >
                  <option value="">— Choisir —</option>
                  {f.options!.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : f.type === "textarea" ? (
                <textarea
                  value={values[f.name]}
                  onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                  rows={3}
                  className={classNames(inputCls, "h-auto py-2")}
                />
              ) : (
                <input
                  type={f.type}
                  value={values[f.name]}
                  onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                  placeholder={f.placeholder}
                  min={f.type === "number" ? 0 : undefined}
                  step={f.type === "number" ? "any" : undefined}
                  className={inputCls}
                />
              )}
            </label>
          ))}
          <label className="block">
            <span className="block font-semibold text-gray-700 mb-1">Commentaire (historique)</span>
            <input
              type="text"
              value={values.commentaire}
              onChange={(e) => setValues({ ...values, commentaire: e.target.value })}
              placeholder="Facultatif"
              className={inputCls}
            />
          </label>
          {error && <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 font-semibold">{error}</div>}
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="h-9 px-4 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 font-semibold">
            Annuler
          </button>
          <button type="submit" className="h-9 px-4 rounded-lg bg-[#6d28d9] hover:bg-purple-800 text-white font-bold">
            Valider le changement
          </button>
        </div>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Preferences                                 */
/* -------------------------------------------------------------------------- */

type SortOption =
  | "created-desc"
  | "created-asc"
  | "depart-asc"
  | "montant-desc"
  | "montant-asc"
  | "poids-desc"
  | "sender-asc"
  | "code-asc";

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "created-desc", label: "Plus récentes" },
  { value: "created-asc", label: "Plus anciennes" },
  { value: "depart-asc", label: "Départ le plus proche" },
  { value: "montant-desc", label: "Montant ↓" },
  { value: "montant-asc", label: "Montant ↑" },
  { value: "poids-desc", label: "Poids ↓" },
  { value: "sender-asc", label: "Expéditeur A → Z" },
  { value: "code-asc", label: "Code" },
];

const LIMIT_OPTIONS = [5, 10, 20, 50, 0]; // 0 = all

type PeriodKey = "all" | "today" | "7d" | "30d" | "month" | "custom";

const PERIOD_OPTIONS: { value: PeriodKey; label: string }[] = [
  { value: "all", label: "Toute période" },
  { value: "today", label: "Aujourd'hui" },
  { value: "7d", label: "7 derniers jours" },
  { value: "30d", label: "30 derniers jours" },
  { value: "month", label: "Ce mois-ci" },
  { value: "custom", label: "Personnalisée…" },
];

interface LanePrefs {
  collapsed: boolean;
  hidden: boolean;
  deleted: boolean;
  sort: SortOption;
  limit: number;
}

interface BoardPrefs {
  order: StageKey[];
  lanes: Record<StageKey, LanePrefs>;
  period: PeriodKey;
  from: string;
  to: string;
  dateField: "created" | "depart";
}

const PREFS_STORAGE_KEY = "envois.kanbanPrefs";

const defaultLanePrefs = (): LanePrefs => ({ collapsed: false, hidden: false, deleted: false, sort: "created-desc", limit: 10 });

function defaultPrefs(): BoardPrefs {
  const lanes = {} as Record<StageKey, LanePrefs>;
  STAGES.forEach((s) => (lanes[s.key] = defaultLanePrefs()));
  return { order: STAGES.map((s) => s.key), lanes, period: "all", from: "", to: "", dateField: "created" };
}

// Merge saved prefs over defaults so stages added later still show up.
function loadPrefs(): BoardPrefs {
  const base = defaultPrefs();
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY);
    if (!raw) return base;
    const saved = JSON.parse(raw) as Partial<BoardPrefs>;
    const known = new Set(base.order);
    const order = (saved.order ?? []).filter((k) => known.has(k));
    base.order.forEach((k) => !order.includes(k) && order.push(k));
    const lanes = { ...base.lanes };
    Object.entries(saved.lanes ?? {}).forEach(([k, v]) => {
      if (known.has(k as StageKey)) lanes[k as StageKey] = { ...defaultLanePrefs(), ...v };
    });
    return { ...base, ...saved, order, lanes };
  } catch {
    return base;
  }
}

const itemDate = (r: ShipmentItem, field: BoardPrefs["dateField"]): Date | null =>
  field === "created" ? parseFrDateTime(r.createdAt) : /^\d{4}-\d{2}-\d{2}$/.test(r.depart) ? new Date(`${r.depart}T00:00`) : null;

function periodRange(p: BoardPrefs): [Date | null, Date | null] {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysAgo = (n: number) => new Date(startOfToday.getTime() - n * 86400000);
  switch (p.period) {
    case "today":
      return [startOfToday, null];
    case "7d":
      return [daysAgo(6), null];
    case "30d":
      return [daysAgo(29), null];
    case "month":
      return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    case "custom":
      return [p.from ? new Date(`${p.from}T00:00`) : null, p.to ? new Date(`${p.to}T23:59:59`) : null];
    default:
      return [null, null];
  }
}

function sortItems(items: ShipmentItem[], sort: SortOption): ShipmentItem[] {
  const created = (r: ShipmentItem) => parseFrDateTime(r.createdAt)?.getTime() ?? 0;
  const cmp: Record<SortOption, (a: ShipmentItem, b: ShipmentItem) => number> = {
    "created-desc": (a, b) => created(b) - created(a),
    "created-asc": (a, b) => created(a) - created(b),
    "depart-asc": (a, b) => a.depart.localeCompare(b.depart),
    "montant-desc": (a, b) => b.montant - a.montant,
    "montant-asc": (a, b) => a.montant - b.montant,
    "poids-desc": (a, b) => b.poids - a.poids,
    "sender-asc": (a, b) => a.sender.localeCompare(b.sender, "fr"),
    "code-asc": (a, b) => a.code.localeCompare(b.code),
  };
  return [...items].sort(cmp[sort]);
}

/* -------------------------------------------------------------------------- */
/*                                    Board                                   */
/* -------------------------------------------------------------------------- */

type DragState = { kind: "card"; id: string; from: StageKey } | { kind: "lane"; key: StageKey } | null;

export default function EnvoisKanban({
  items,
  onOpen,
  onMove,
}: {
  items: ShipmentItem[];
  onOpen: (r: ShipmentItem) => void;
  onMove: (id: string, stage: StageKey, patch: Partial<ShipmentItem>, note: string) => void;
}) {
  const [prefs, setPrefs] = useState<BoardPrefs>(loadPrefs);
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify(loadPrefs()));
  const [search, setSearch] = useState<Partial<Record<StageKey, string>>>({});
  const [extra, setExtra] = useState<Partial<Record<StageKey, number>>>({});
  const [menuLane, setMenuLane] = useState<StageKey | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<StageKey | null>(null);
  const [blocksMenuOpen, setBlocksMenuOpen] = useState(false);
  const [dropTarget, setDropTarget] = useState<StageKey | null>(null);
  const [pending, setPending] = useState<{ item: ShipmentItem; from: StageKey; to: StageKey } | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const drag = useRef<DragState>(null);

  const dirty = JSON.stringify(prefs) !== savedSnapshot;

  const updateLane = (key: StageKey, patch: Partial<LanePrefs>) =>
    setPrefs((p) => ({ ...p, lanes: { ...p.lanes, [key]: { ...p.lanes[key], ...patch } } }));

  const moveLane = (key: StageKey, beforeKey: StageKey | null) =>
    setPrefs((p) => {
      const order = p.order.filter((k) => k !== key);
      const idx = beforeKey ? order.indexOf(beforeKey) : order.length;
      order.splice(idx < 0 ? order.length : idx, 0, key);
      return { ...p, order };
    });

  const shiftLane = (key: StageKey, delta: -1 | 1) => {
    const visible = prefs.order.filter((k) => !prefs.lanes[k].hidden && !prefs.lanes[k].deleted);
    const i = visible.indexOf(key);
    const target = visible[i + delta];
    if (!target) return;
    // Moving right = insert before the lane after the target.
    moveLane(key, delta === -1 ? target : visible[i + 2] ?? null);
  };

  const savePrefs = () => {
    try {
      localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
    } catch {}
    setSavedSnapshot(JSON.stringify(prefs));
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1800);
  };

  const resetPrefs = () => {
    try {
      localStorage.removeItem(PREFS_STORAGE_KEY);
    } catch {}
    const d = defaultPrefs();
    setPrefs(d);
    setSavedSnapshot(JSON.stringify(d));
    setSearch({});
    setExtra({});
  };

  // Period filter applies to the whole board.
  const inPeriod = useMemo(() => {
    const [start, end] = periodRange(prefs);
    return items.filter((r) => {
      if (!start && !end) return true;
      const d = itemDate(r, prefs.dateField);
      if (!d) return true;
      return (!start || d >= start) && (!end || d <= end);
    });
  }, [items, prefs]);

  const byStage = useMemo(() => {
    const map = {} as Record<StageKey, ShipmentItem[]>;
    STAGES.forEach((s) => (map[s.key] = []));
    inPeriod.forEach((r) => map[stageOf(r)].push(r));
    return map;
  }, [inPeriod]);

  const visibleLanes = prefs.order.filter((k) => !prefs.lanes[k].hidden && !prefs.lanes[k].deleted);
  const hiddenLanes = prefs.order.filter((k) => prefs.lanes[k].hidden && !prefs.lanes[k].deleted);
  const deletedLanes = prefs.order.filter((k) => prefs.lanes[k].deleted);
  const offBoardCount = [...hiddenLanes, ...deletedLanes].reduce((n, k) => n + byStage[k].length, 0);

  /* ------------------------------- Drag & drop ------------------------------ */

  const onCardDragStart = (e: React.DragEvent, r: ShipmentItem) => {
    drag.current = { kind: "card", id: r.id, from: stageOf(r) };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", r.id);
    e.stopPropagation();
  };

  const onLaneDragStart = (e: React.DragEvent, key: StageKey) => {
    drag.current = { kind: "lane", key };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  };

  const onLaneDragOver = (e: React.DragEvent, key: StageKey) => {
    const d = drag.current;
    if (!d) return;
    if ((d.kind === "card" && d.from !== key) || (d.kind === "lane" && d.key !== key)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dropTarget !== key) setDropTarget(key);
    }
  };

  const onLaneDrop = (e: React.DragEvent, key: StageKey) => {
    e.preventDefault();
    const d = drag.current;
    drag.current = null;
    setDropTarget(null);
    if (!d) return;
    if (d.kind === "lane") {
      moveLane(d.key, key);
      return;
    }
    const item = items.find((r) => r.id === d.id);
    if (item && d.from !== key) setPending({ item, from: d.from, to: key });
  };

  const onDragEnd = () => {
    drag.current = null;
    setDropTarget(null);
  };

  /* --------------------------------- Render --------------------------------- */

  const selectCls = "h-8 px-2 bg-white border border-gray-200 rounded-lg text-xs text-[#1f1b2e] focus:outline-none focus:ring-2 focus:ring-purple-300";

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Board toolbar */}
      <div className="flex items-center gap-2 flex-wrap px-3.5 pt-3 text-xs">
        <select
          value={prefs.period}
          onChange={(e) => setPrefs({ ...prefs, period: e.target.value as PeriodKey })}
          className={selectCls}
          title="Période à afficher"
        >
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {prefs.period === "custom" && (
          <>
            <input type="date" value={prefs.from} onChange={(e) => setPrefs({ ...prefs, from: e.target.value })} className={selectCls} title="Du" />
            <span className="text-gray-400">→</span>
            <input type="date" value={prefs.to} onChange={(e) => setPrefs({ ...prefs, to: e.target.value })} className={selectCls} title="Au" />
          </>
        )}
        <select
          value={prefs.dateField}
          onChange={(e) => setPrefs({ ...prefs, dateField: e.target.value as BoardPrefs["dateField"] })}
          className={selectCls}
          title="Date utilisée pour la période"
        >
          <option value="created">selon la date de création</option>
          <option value="depart">selon la date de départ</option>
        </select>

        <div className="relative">
          <button
            type="button"
            onClick={() => setBlocksMenuOpen(!blocksMenuOpen)}
            className="h-8 inline-flex items-center gap-1 px-2.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg font-semibold text-gray-700"
          >
            <EyeIcon className="w-3.5 h-3.5" />
            Blocs
            <span className="px-1.5 bg-purple-100 text-purple-800 rounded-full text-[10px] font-bold">
              {visibleLanes.length}/{STAGES.length}
            </span>
          </button>
          {blocksMenuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setBlocksMenuOpen(false)} />
              <div className="absolute left-0 top-9 z-40 w-64 bg-white border border-gray-200 rounded-xl shadow-xl p-1.5">
                {prefs.order
                  .filter((k) => !prefs.lanes[k].deleted)
                  .map((k) => (
                    <label key={k} className="flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-purple-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!prefs.lanes[k].hidden}
                        onChange={() => updateLane(k, { hidden: !prefs.lanes[k].hidden })}
                        className="w-3.5 h-3.5 accent-purple-700"
                      />
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STAGES.find((s) => s.key === k)!.dot }} />
                      <span className="flex-1">{stageTitle(k)}</span>
                      <span className="text-[10px] text-gray-400">{byStage[k].length}</span>
                    </label>
                  ))}
                {deletedLanes.length > 0 && (
                  <div className="mt-1 pt-1 border-t border-gray-100">
                    <div className="px-2.5 py-1 text-[10px] uppercase font-bold text-gray-400">Blocs supprimés</div>
                    {deletedLanes.map((k) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => updateLane(k, { deleted: false, hidden: false })}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-purple-50 text-left"
                      >
                        <ArrowUturnLeftIcon className="w-3.5 h-3.5 text-purple-700" />
                        <span className="flex-1">{stageTitle(k)}</span>
                        <span className="text-[10px] font-semibold text-purple-700">Restaurer</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {offBoardCount > 0 && (
          <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
            {offBoardCount} expédition{offBoardCount > 1 ? "s" : ""} dans des blocs masqués ou supprimés
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {savedFlash && (
            <span className="text-emerald-700 font-semibold inline-flex items-center gap-1">
              Préférences enregistrées <CheckIcon className="w-3.5 h-3.5" />
            </span>
          )}
          <button
            type="button"
            onClick={resetPrefs}
            className="h-8 px-2.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg font-semibold text-gray-600"
            title="Revenir à la disposition par défaut"
          >
            Réinitialiser
          </button>
          <button
            type="button"
            onClick={savePrefs}
            disabled={!dirty}
            className={classNames(
              "h-8 px-3 rounded-lg font-bold",
              dirty ? "bg-[#6d28d9] hover:bg-purple-800 text-white" : "bg-gray-100 text-gray-400 cursor-default"
            )}
            title="Enregistre l'ordre, l'affichage, les tris, limites et la période"
          >
            {dirty ? "Enregistrer mes préférences" : "Préférences à jour"}
          </button>
        </div>
      </div>

      {/* Lanes */}
      <div className="flex-1 min-h-0 overflow-x-auto tracking-scrollbar p-3.5 flex gap-3.5 items-start">
        {visibleLanes.map((key, laneIdx) => {
          const stage = STAGES.find((s) => s.key === key)!;
          const lp = prefs.lanes[key];
          const q = (search[key] ?? "").trim().toLowerCase();
          const all = byStage[key];
          const matched = sortItems(
            q
              ? all.filter((r) =>
                  [r.code, r.awb, r.sender, r.receiver, r.phone, r.from, r.to, r.mode, r.corridor]
                    .join(" ")
                    .toLowerCase()
                    .includes(q)
                )
              : all,
            lp.sort
          );
          const limit = lp.limit === 0 ? Infinity : lp.limit + (extra[key] ?? 0);
          const shown = matched.slice(0, limit);
          const isDrop = dropTarget === key;
          const laneDrag = drag.current?.kind === "lane";

          if (lp.collapsed) {
            return (
              <div
                key={key}
                onDragOver={(e) => onLaneDragOver(e, key)}
                onDragLeave={() => setDropTarget(null)}
                onDrop={(e) => onLaneDrop(e, key)}
                className={classNames(
                  "w-11 shrink-0 self-stretch max-h-[520px] bg-[#fbf9ff] border rounded-xl py-3 flex flex-col items-center gap-2 cursor-pointer transition-colors",
                  isDrop ? "border-purple-500 bg-purple-50 ring-2 ring-purple-300" : "border-purple-100 hover:bg-purple-50"
                )}
                onClick={() => updateLane(key, { collapsed: false })}
                title={`Déplier « ${stage.title} »`}
                draggable
                onDragStart={(e) => onLaneDragStart(e, key)}
                onDragEnd={onDragEnd}
              >
                <ChevronDoubleRightIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.dot }} />
                <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-800 text-[10px] font-bold">{all.length}</span>
                <span className="font-bold text-xs text-gray-800 [writing-mode:vertical-rl] rotate-180 whitespace-nowrap">{stage.title}</span>
              </div>
            );
          }

          return (
            <div
              key={key}
              onDragOver={(e) => onLaneDragOver(e, key)}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropTarget(null);
              }}
              onDrop={(e) => onLaneDrop(e, key)}
              className={classNames(
                "w-72 shrink-0 bg-[#fbf9ff] border rounded-xl p-3 flex flex-col max-h-[640px] transition-colors",
                isDrop && !laneDrag && "border-purple-500 bg-purple-50/70 ring-2 ring-purple-300",
                isDrop && laneDrag && "border-l-4 border-l-purple-500 border-purple-100",
                !isDrop && "border-purple-100"
              )}
            >
              {/* Header — drag handle for reordering */}
              <div
                draggable
                onDragStart={(e) => onLaneDragStart(e, key)}
                onDragEnd={onDragEnd}
                className="flex items-center gap-2 pb-2 border-b border-purple-100 cursor-grab active:cursor-grabbing"
                title="Glisser pour déplacer le bloc"
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: stage.dot }} />
                <span className="font-bold text-xs text-gray-900 truncate">{stage.title}</span>
                <span className="ml-auto px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-[10px] font-bold shrink-0">
                  {q ? `${matched.length}/${all.length}` : all.length}
                </span>
                <button
                  type="button"
                  onClick={() => updateLane(key, { collapsed: true })}
                  className="p-0.5 rounded hover:bg-purple-100 text-gray-500"
                  title="Réduire"
                >
                  <ChevronDoubleLeftIcon className="w-3.5 h-3.5" />
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuLane(menuLane === key ? null : key);
                      setConfirmDelete(null);
                    }}
                    className="p-0.5 rounded hover:bg-purple-100 text-gray-500"
                    title="Options du bloc"
                  >
                    <EllipsisHorizontalIcon className="w-4 h-4" />
                  </button>
                  {menuLane === key && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setMenuLane(null)} />
                      <div className="absolute right-0 top-6 z-40 w-52 bg-white border border-gray-200 rounded-xl shadow-xl p-1 text-xs">
                        <MenuItem icon={ChevronDoubleLeftIcon} label="Réduire" onClick={() => { updateLane(key, { collapsed: true }); setMenuLane(null); }} />
                        <MenuItem icon={ChevronLeftIcon} label="Déplacer à gauche" disabled={laneIdx === 0} onClick={() => shiftLane(key, -1)} />
                        <MenuItem icon={ChevronRightIcon} label="Déplacer à droite" disabled={laneIdx === visibleLanes.length - 1} onClick={() => shiftLane(key, 1)} />
                        <MenuItem icon={EyeSlashIcon} label="Masquer" onClick={() => { updateLane(key, { hidden: true }); setMenuLane(null); }} />
                        {confirmDelete === key ? (
                          <div className="m-1 p-2 rounded-lg bg-rose-50 border border-rose-200">
                            <p className="text-rose-800 mb-2">
                              Supprimer ce bloc du tableau ?{" "}
                              {all.length > 0 && `Ses ${all.length} expédition(s) ne seront plus visibles ici (elles restent dans la vue Tableau).`}
                            </p>
                            <div className="flex gap-1.5">
                              <button type="button" onClick={() => setConfirmDelete(null)} className="flex-1 h-7 rounded border border-gray-300 bg-white font-semibold">
                                Non
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  updateLane(key, { deleted: true });
                                  setMenuLane(null);
                                  setConfirmDelete(null);
                                }}
                                className="flex-1 h-7 rounded bg-rose-600 hover:bg-rose-700 text-white font-bold"
                              >
                                Supprimer
                              </button>
                            </div>
                          </div>
                        ) : (
                          <MenuItem icon={TrashIcon} label="Supprimer le bloc" danger onClick={() => setConfirmDelete(key)} />
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Lane tools: search, sort, limit */}
              <div className="py-2 space-y-1.5">
                <div className="relative">
                  <MagnifyingGlassIcon className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={search[key] ?? ""}
                    onChange={(e) => setSearch({ ...search, [key]: e.target.value })}
                    placeholder="Rechercher dans le bloc…"
                    className="w-full h-7 pl-6 pr-2 bg-white border border-gray-200 rounded-md text-[11px] focus:outline-none focus:ring-2 focus:ring-purple-300"
                  />
                </div>
                <div className="flex gap-1.5">
                  <select
                    value={lp.sort}
                    onChange={(e) => updateLane(key, { sort: e.target.value as SortOption })}
                    className="flex-1 min-w-0 h-7 px-1.5 bg-white border border-gray-200 rounded-md text-[11px]"
                    title="Trier"
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={lp.limit}
                    onChange={(e) => {
                      updateLane(key, { limit: Number(e.target.value) });
                      setExtra({ ...extra, [key]: 0 });
                    }}
                    className="w-[72px] h-7 px-1.5 bg-white border border-gray-200 rounded-md text-[11px]"
                    title="Nombre à afficher"
                  >
                    {LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n === 0 ? "Tous" : `${n} max`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Cards */}
              <div className="flex-1 min-h-[60px] overflow-y-auto tracking-scrollbar space-y-2.5 pr-1">
                {shown.map((r) => {
                  const L = LOGI_STYLE[r.logi];
                  return (
                    <div
                      key={r.id}
                      draggable
                      onDragStart={(e) => onCardDragStart(e, r)}
                      onDragEnd={onDragEnd}
                      onClick={() => onOpen(r)}
                      className="p-3 bg-white border border-gray-200/80 rounded-lg shadow-2xs hover:shadow-xs hover:border-purple-200 transition-shadow cursor-grab active:cursor-grabbing"
                      title="Cliquer pour voir · glisser pour changer de bloc"
                    >
                      <div className="flex justify-between items-start gap-1">
                        <span className="font-mono text-xs font-semibold text-emerald-800">{r.code}</span>
                        <span className="font-bold text-xs whitespace-nowrap">{money(r.montant)}</span>
                      </div>
                      <div className="font-bold text-xs text-gray-900 mt-2 truncate">{r.sender}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                        {r.from} → {r.to} ({r.mode})
                      </div>
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: PAY_STYLE[r.pay].bg, color: PAY_STYLE[r.pay].fg }}>
                          {r.pay}
                        </span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: L.bg, color: L.fg }}>
                          {r.logi}
                        </span>
                        <span className="text-[10px] text-gray-400 ml-auto">{kg(r.poids)}</span>
                      </div>
                    </div>
                  );
                })}
                {matched.length > shown.length && (
                  <button
                    type="button"
                    onClick={() => setExtra({ ...extra, [key]: (extra[key] ?? 0) + (lp.limit || 10) })}
                    className="w-full h-8 rounded-lg border border-dashed border-purple-300 text-purple-700 font-semibold text-[11px] hover:bg-purple-50"
                  >
                    Afficher plus ({matched.length - shown.length} restante{matched.length - shown.length > 1 ? "s" : ""})
                  </button>
                )}
                {matched.length === 0 && (
                  <div
                    className={classNames(
                      "p-4 text-center text-xs border border-dashed rounded-lg",
                      isDrop ? "border-purple-400 text-purple-700" : "border-gray-200 text-gray-400"
                    )}
                  >
                    {isDrop ? "Déposer ici" : q ? "Aucun résultat" : "Aucun envoi"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {visibleLanes.length === 0 && (
          <div className="m-auto p-8 text-center text-xs text-gray-500">
            Tous les blocs sont masqués. Utilise le menu <strong>Blocs</strong> ou <strong>Réinitialiser</strong>.
          </div>
        )}
      </div>

      {pending && (
        <TransitionModal
          item={pending.item}
          from={pending.from}
          to={pending.to}
          onCancel={() => setPending(null)}
          onConfirm={(patch, note) => {
            onMove(pending.item.id, pending.to, patch, note);
            setPending(null);
          }}
        />
      )}
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={classNames(
        "w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left font-semibold",
        disabled ? "text-gray-300 cursor-default" : danger ? "text-rose-700 hover:bg-rose-50" : "text-gray-700 hover:bg-purple-50"
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
